import React, { useState, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Navigate, useNavigate, useLocation, Link as RouterLink } from 'react-router-dom';

import Form from "react-validation/build/form";
import CheckButton from "react-validation/build/button";

import { login } from "../../../actions/auth";

import {
  Box,
  Typography,
  TextField,
  InputAdornment,
  IconButton,
  Alert,
  Stack,
  alpha,
  useTheme,
  CircularProgress,
  Link,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import PersonOutlineRoundedIcon from '@mui/icons-material/PersonOutlineRounded';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';
import ShieldOutlinedIcon from '@mui/icons-material/ShieldOutlined';

export default function LoginForm() {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const dispatch = useDispatch();

  const searchParams = new URLSearchParams(location.search);
  const redirectUrl =
    location.state?.from?.pathname ||
    searchParams.get("redirect") ||
    "/dashboard/app";

  const form = useRef();
  const checkBtn = useRef();

  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleClickShowPassword = () => setShowPassword((s) => !s);
  const handleMouseDownPassword = (e) => e.preventDefault();

  const { isLoggedIn } = useSelector(state => state.auth || {});
  const { message } = useSelector(state => state.message || {});

  const onChangeUsername = (e) => setUsername(e.target.value);
  const onChangePassword = (e) => setPassword(e.target.value);

  const handleLogin = (e) => {
    e.preventDefault();
    setLoading(true);

    form.current.validateAll();

    if (checkBtn.current.context._errors.length === 0) {
      dispatch(login(username, password))
        .then(() => {
          navigate(redirectUrl, { replace: true });
        })
        .catch(() => {
          setLoading(false);
        });
    } else {
      setLoading(false);
    }
  };

  if (isLoggedIn) {
    return <Navigate to={redirectUrl} replace />;
  }

  return (
    <Box sx={{ width: '100%' }}>
      {message && (
        <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
          {message}
        </Alert>
      )}

      <Form onSubmit={handleLogin} ref={form}>
        <Stack spacing={2.5}>
          {/* Username Field */}
          <Box>
            <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', mb: 0.75, display: 'block' }}>
              Username
            </Typography>
            <TextField
              fullWidth
              size="medium"
              placeholder="Enter your username"
              value={username}
              onChange={onChangeUsername}
              required
              autoComplete="username"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <PersonOutlineRoundedIcon sx={{ color: 'primary.main', fontSize: 22 }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '12px',
                  backgroundColor: alpha(theme.palette.grey[500], 0.04),
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    backgroundColor: alpha(theme.palette.grey[500], 0.08),
                  },
                  '&.Mui-focused': {
                    backgroundColor: 'background.paper',
                    boxShadow: `0 0 0 3px ${alpha(theme.palette.primary.main, 0.15)}`,
                  },
                },
              }}
            />
          </Box>

          {/* Password Field */}
          <Box>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.75 }}>
              <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', display: 'block' }}>
                Password
              </Typography>
              <Link
                component={RouterLink}
                to="/forgot-password"
                variant="caption"
                sx={{
                  color: 'primary.main',
                  fontWeight: 600,
                  textDecoration: 'none',
                  cursor: 'pointer',
                  '&:hover': { textDecoration: 'underline' }
                }}
              >
                Forgot password?
              </Link>
            </Stack>
            <TextField
              fullWidth
              size="medium"
              placeholder="Enter your secure password"
              type={showPassword ? "text" : "password"}
              value={password}
              onChange={onChangePassword}
              required
              autoComplete="current-password"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LockOutlinedIcon sx={{ color: 'primary.main', fontSize: 22 }} />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={handleClickShowPassword}
                      onMouseDown={handleMouseDownPassword}
                      edge="end"
                      size="small"
                      sx={{ color: 'text.secondary' }}
                    >
                      {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '12px',
                  backgroundColor: alpha(theme.palette.grey[500], 0.04),
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    backgroundColor: alpha(theme.palette.grey[500], 0.08),
                  },
                  '&.Mui-focused': {
                    backgroundColor: 'background.paper',
                    boxShadow: `0 0 0 3px ${alpha(theme.palette.primary.main, 0.15)}`,
                  },
                },
              }}
            />
          </Box>

          {/* Submit Button */}
          <LoadingButton
            fullWidth
            size="large"
            type="submit"
            variant="contained"
            loading={loading}
            loadingIndicator={
              <Stack direction="row" spacing={1} alignItems="center">
                <CircularProgress color="inherit" size={18} />
                <Typography variant="button" sx={{ color: '#fff', fontSize: '0.9rem' }}>
                  Verifying...
                </Typography>
              </Stack>
            }
            endIcon={!loading && <ArrowForwardRoundedIcon />}
            sx={{
              mt: 1.5,
              py: 1.5,
              borderRadius: '12px',
              fontSize: '1rem',
              fontWeight: 700,
              textTransform: 'none',
              background: 'linear-gradient(135deg, #0B3B60 0%, #0369A1 100%)',
              boxShadow: '0 8px 20px rgba(11, 59, 96, 0.28)',
              transition: 'all 0.2s ease-in-out',
              '&:hover': {
                background: 'linear-gradient(135deg, #072740 0%, #0284C7 100%)',
                boxShadow: '0 12px 28px rgba(11, 59, 96, 0.38)',
                transform: 'translateY(-1px)',
              },
            }}
          >
            Sign In to Portal
          </LoadingButton>

          {/* Security Assurance */}
          <Stack direction="row" spacing={1} alignItems="center" justifyContent="center" sx={{ pt: 1 }}>
            <ShieldOutlinedIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
            <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 500 }}>
              Encrypted Non-Interest Financial System • MTC-FS
            </Typography>
          </Stack>
        </Stack>

        <CheckButton style={{ display: "none" }} ref={checkBtn} />
      </Form>
    </Box>
  );
}
